// Globals
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'react-i18next';

// Models
import { IFullAddress } from '@/models/addresses.model';
import { IProgressStep, IProperty } from '@/models/property.model';

// Components
import { Tooltip } from '@/ui';
import StepTag from '../components/StepTag/StepTag';
import { StyledLink, StyledStep, StyledStepsWrap } from '../components/Styled';

// Helpers
import { getEntityId, renderPercentage } from '@/utils';

type TGetPropertiesTableColumns = {
  t: TFunction;
  isSold?: boolean;
};

export const getPropertiesTableColumns = ({
  t,
  isSold = false,
}: TGetPropertiesTableColumns): ColumnsType<IProperty> => {
  const baseColumns: ColumnsType<IProperty> = [
    {
      title: t('property_id'),
      dataIndex: 'PK',
      key: 'PK',
      width: 90,
      render: (_, record) => (
        <Tooltip title={getEntityId(record.PK)}>
          <StyledLink as="span">{getEntityId(record.PK)?.slice(0, 4)}</StyledLink>
        </Tooltip>
      ),
    },

    {
      title: t('property_address'),
      dataIndex: 'address',
      key: 'address',
      width: 180,
      render: (address: IFullAddress) => <span>{address?.full_address}</span>,
    },
    {
      title: t('property_gnr'),
      dataIndex: 'gnr',
      key: 'gnr',
      width: 90,
      render: (gnr) => <span>{gnr}</span>,
    },
    {
      title: t('property_bnr'),
      dataIndex: 'bnr',
      key: 'bnr',
      width: 90,
      render: (bnr) => <span>{bnr}</span>,
    },
    {
      title: t('property_snr_andelsnr'),
      key: 'property_seksjons',
      width: 90,
      render: ({ snr, housing_association_share_id, is_housing_association }) => {
        return <span>{!is_housing_association && snr !== '0' ? snr : housing_association_share_id}</span>;
      },
    },
    {
      title: t('property_type'),
      dataIndex: 'property_type',
      key: 'property_type',
      width: 225,
      render: (property_type) => <StyledStep>{t(`property_type_${property_type}`, { ns: 'common' })}</StyledStep>,
    },
    {
      title: t('property_email'),
      dataIndex: 'user_email',
      key: 'user_email',
      width: 200,
      render: (email) => <span>{email}</span>,
    },
    {
      title: t('property_owners'),
      dataIndex: 'owners',
      key: 'owners',
      width: 150,
      render: (_, record) => (
        <span>
          {`${record?.user_name ?? ''} ${renderPercentage(
            record?.ownership_share?.numerator / record?.ownership_share?.denominator,
          )}`}
        </span>
      ),
    },
    {
      title: t('property_phone'),
      dataIndex: 'user_phone_number',
      key: 'user_phone_number',
      width: 150,
      render: (phone) => <span>{phone}</span>,
    },
    ...(isSold
      ? []
      : [
          {
            title: t('property_sum_orders'),
            dataIndex: 'total_orders_sum',
            key: 'total_orders_sum',
            width: 120,
            render: (sum: string) => <span>{sum}</span>,
          },
        ]),
    {
      title: t('property_established'),
      key: 'established',
      dataIndex: 'estimated_year_built',
      width: 120,
      render: (estimated_year_built) => <span>{estimated_year_built}</span>,
    },
  ];

  if (isSold) {
    return [
      ...baseColumns,
      {
        title: t('property_estimated_nok'),
        key: 'estimated_nok',
        dataIndex: 'final_valuation_price',
        width: 150,
        render: (final_valuation_price) => <span>{final_valuation_price ?? '-'}</span>,
      },
      {
        title: t('property_sold_price'),
        key: 'sold_price',
        dataIndex: 'final_sold_price',
        width: 150,
        render: (final_sold_price) => <span>{final_sold_price ?? '-'}</span>,
      },
      {
        title: t('property_sum_orders'),
        key: 'sum_orders',
        dataIndex: 'total_orders_sum',
        width: 150,
        render: (sum) => <span>{sum ?? '-'}</span>,
      },
    ];
  }

  return [
    ...baseColumns,
    {
      title: t('property_progress'),
      key: 'progress',
      dataIndex: 'progress',
      width: 260,
      render: (progress: IProgressStep[]) => (
        <StyledStepsWrap>
          {progress?.map((step, idx) => (
            <StepTag key={step.step_name} number={idx + 1} status={step.status} />
          ))}
        </StyledStepsWrap>
      ),
    },
  ];
};
